import { makeAutoObservable, observable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { canUseDataMapper } from "../rbac";
import { dataMapperSelection, dataMapperService } from "../dataMapperServices";
import {
  isRecordChanged,
  sameId,
  visibleToSelection,
  type DataMapperLookupTable,
  type DataMapperScope,
} from "../dataMapper.types";
import { bindSelectionToSignedInUser, editorFailure, groupByScope } from "../shared/editorSupport";
import {
  LOOKUP_ENTRIES_MAX,
  LOOKUP_ROWS_PER_PAGE,
  duplicateSourceCount,
  entriesProblem,
  newRowKey,
  parsePastedEntries,
  rowsFromEntries,
  toEntriesPayload,
  type LookupEntryRow,
} from "./lookupEntries";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** How a save ended. `conflict` = 409 record-changed: the form closes and the list reloads. */
export type LookupSaveOutcome = "saved" | "conflict" | "failed";

export const lookupRecordChangedNotice = (name: string): string =>
  `Someone else saved "${name}" while you were editing it, so your changes were not saved. ` +
  "The list has been reloaded with their version; open the table again to apply yours.";

/**
 * Data Mapper → Lookup Tables: the lookup tables the open profile sees (its
 * own, its client's, global), and an editor for one: name, description and
 * its entries (source value → target value), with rows added, removed and
 * pasted from a spreadsheet as two columns. A Value Lookup step on the
 * Transform screen picks these tables.
 *
 * Calls `data-mapper-lookup-tables` through its ServiceStore. Every call names
 * the selection; a by-id call outside it is not found. A patch carries
 * `expectUpdatedAt` (a concurrent save is a 409 `record-changed`) and, since
 * the editor always sends `entries`, replaces them all — as the API does.
 *
 * Plain MobX class (no ViewStore base). The tables stay in the ServiceStore;
 * this holds only the form. `rows` is the form's working copy of the entries
 * (up to 50,000), kept as one immutable array (`observable.ref`) rather than
 * 50,000 observable objects.
 */
class DataMapperLookupTablesStore {
  rootStore: RootStore;

  loading = false;
  loaded = false;
  error: string | null = null;
  actionError: string | null = null;
  notice: string | null = null;

  // ---- The editor form ----
  formOpen = false;
  editingTableId: string | null = null;
  /** The `updatedAt` the edit was loaded from — sent back as `expectUpdatedAt`. */
  editingUpdatedAt: string | null = null;
  formName = "";
  formDescription = "";
  formScope: DataMapperScope = "profile";
  rows: LookupEntryRow[] = [];
  /** The grid page shown (0-based). */
  page = 0;
  /** An edit's entries are being read (`get`). */
  entriesLoading = false;
  /**
   * The rows hold the table's entries: always for a new table, for an edit
   * only once its `get` answered. A save sends `entries`, which REPLACES them
   * all, so an edit whose read failed must never save (Codex r1: it would
   * have sent `[]` and wiped the table).
   */
  entriesRead = false;
  saving = false;
  formError: string | null = null;

  // ---- Paste box ----
  pasteOpen = false;
  pasteText = "";
  pasteMessage: string | null = null;

  private loadSeq = 0;
  /** Bumped when the form is reset or replaced: an answer for the old form touches nothing. */
  private formEpoch = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<this, "loadSeq" | "formEpoch">(this, {
      rootStore: false,
      rows: observable.ref,
      loadSeq: false,
      formEpoch: false,
    });
  }

  private get service() {
    return dataMapperService(this.rootStore, "data-mapper-lookup-tables");
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get profileId(): string | null {
    return this.selection?.selectedProfileId ?? null;
  }

  get clientId(): string | null {
    return this.selection?.selectedClientId ?? null;
  }

  /** The tables the open profile sees — a computed view over the ServiceStore cache. */
  get tables(): DataMapperLookupTable[] {
    if (!this.profileId) return [];
    const rows = (this.service?.entities ?? []) as unknown as DataMapperLookupTable[];
    return rows.filter((r) => visibleToSelection(r, this.clientId, this.profileId));
  }

  get groups() {
    return groupByScope(this.tables);
  }

  tableById(id: string | null): DataMapperLookupTable | null {
    if (!id) return null;
    return this.tables.find((t) => sameId(t.id, id)) ?? null;
  }

  get editingTable(): DataMapperLookupTable | null {
    return this.tableById(this.editingTableId);
  }

  /** Rows that hold something (an entirely empty row is not an entry). */
  get entryCount(): number {
    return this.rows.filter((r) => r.source.trim() || r.target.trim()).length;
  }

  get duplicateCount(): number {
    return duplicateSourceCount(this.rows);
  }

  get entriesProblem(): string | null {
    return entriesProblem(this.rows);
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.rows.length / LOOKUP_ROWS_PER_PAGE));
  }

  /** The rows on the page shown. */
  get pageRows(): LookupEntryRow[] {
    const start = this.page * LOOKUP_ROWS_PER_PAGE;
    return this.rows.slice(start, start + LOOKUP_ROWS_PER_PAGE);
  }

  get isFormValid(): boolean {
    return (
      this.formName.trim().length > 0 && !this.entriesProblem && this.entriesRead && !this.entriesLoading && !this.saving
    );
  }

  // ---- Loading -------------------------------------------------------------

  /** The open profile's lookup tables (with entry counts). Every find names the selection. */
  async load(): Promise<void> {
    const seq = ++this.loadSeq;
    bindSelectionToSignedInUser(this.rootStore);
    const selection = this.selection;
    if (!this.canUse || !selection?.hasProfile) {
      this.loading = false;
      this.loaded = false;
      this.error = null;
      return;
    }
    const query = { ...selection.selectionQuery };
    this.loading = true;
    this.error = null;
    // The reset after a sign-out bumps loadSeq, but the epoch moves first: a
    // find ended in between would still pass the seq check and latch `loaded`.
    const ended = sessionMark(this.rootStore);
    try {
      if (!this.service) throw new Error("The Data Mapper service is not available in this build.");
      await this.service.find({ query });
    } catch (err) {
      runInAction(() => {
        if (seq === this.loadSeq)
          { if (!isSessionEnded(err)) this.error = editorFailure(this.rootStore, err, query.profileId ?? null, "The lookup tables could not be loaded."); }
      });
    } finally {
      runInAction(() => {
        if (seq === this.loadSeq && !ended()) {
          this.loading = false;
          this.loaded = true;
        }
      });
    }
  }

  // ---- The form ------------------------------------------------------------

  private openForm(table: DataMapperLookupTable | null) {
    this.formEpoch++;
    this.formOpen = true;
    this.editingTableId = table?.id ?? null;
    this.editingUpdatedAt = table?.updatedAt ?? null;
    this.formName = table?.name ?? "";
    this.formDescription = table?.description ?? "";
    this.formScope = table?.scope ?? "profile";
    this.rows = [];
    this.page = 0;
    this.formError = null;
    this.entriesLoading = false;
    this.entriesRead = false;
    this.closePaste();
  }

  startCreate() {
    if (this.saving) return;
    this.openForm(null);
    this.rows = [{ key: newRowKey(), source: "", target: "" }];
    this.entriesRead = true;
  }

  /** Open a table for editing: its entries are read with `get` (find carries only the count). */
  async startEdit(table: DataMapperLookupTable): Promise<void> {
    if (this.saving) return;
    this.openForm(table);
    const service = this.service;
    const query = { ...(this.selection?.selectionQuery ?? {}) };
    if (!service) {
      this.formError = "The Data Mapper service is not available in this build.";
      return;
    }
    const epoch = this.formEpoch;
    this.entriesLoading = true;
    try {
      const full = (await service.get(table.id, { query })) as unknown as DataMapperLookupTable | null;
      if (epoch !== this.formEpoch) return;
      if (!Array.isArray(full?.entries)) throw new Error(`The entries of "${table.name}" could not be read.`);
      const entries = full.entries;
      runInAction(() => {
        // The read is the version being edited: its updatedAt is what a save expects.
        this.editingUpdatedAt = full.updatedAt ?? this.editingUpdatedAt;
        this.rows = rowsFromEntries(entries);
        this.entriesRead = true;
      });
    } catch (err) {
      if (epoch !== this.formEpoch) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.formError =
            editorFailure(this.rootStore, err, query.profileId ?? null, `The entries of "${table.name}" could not be read.`) +
            " Nothing can be saved until they are: close the form and open the table again.";
        }
      });
    } finally {
      runInAction(() => {
        if (epoch === this.formEpoch) this.entriesLoading = false;
      });
    }
  }

  closeForm() {
    this.formEpoch++;
    this.formOpen = false;
    this.editingTableId = null;
    this.editingUpdatedAt = null;
    this.rows = [];
    this.page = 0;
    this.formError = null;
    this.entriesLoading = false;
    this.closePaste();
  }

  /** Every edit is refused while a save is in flight: its answer closes the form. */
  private get frozen(): boolean {
    return this.saving || this.entriesLoading || !this.entriesRead;
  }

  setFormName(v: string) {
    if (this.frozen) return;
    this.formName = v;
  }

  setFormDescription(v: string) {
    if (this.frozen) return;
    this.formDescription = v;
  }

  setFormScope(v: DataMapperScope) {
    if (this.frozen || this.editingTableId) return; // a table does not move between scopes
    this.formScope = v;
  }

  setPage(page: number) {
    this.page = Math.min(Math.max(0, page), this.pageCount - 1);
  }

  /** A new empty row at the end; the grid shows the page it is on. */
  addRow() {
    if (this.frozen) return;
    this.rows = [...this.rows, { key: newRowKey(), source: "", target: "" }];
    this.page = this.pageCount - 1;
  }

  removeRow(key: string) {
    if (this.frozen) return;
    this.rows = this.rows.filter((r) => r.key !== key);
    this.setPage(this.page);
  }

  setRowSource(key: string, source: string) {
    if (this.frozen) return;
    this.rows = this.rows.map((r) => (r.key === key ? { ...r, source } : r));
  }

  setRowTarget(key: string, target: string) {
    if (this.frozen) return;
    this.rows = this.rows.map((r) => (r.key === key ? { ...r, target, targetIsNull: false } : r));
  }

  // ---- Paste ---------------------------------------------------------------

  openPaste() {
    if (this.frozen) return;
    this.pasteOpen = true;
    this.pasteMessage = null;
  }

  closePaste() {
    this.pasteOpen = false;
    this.pasteText = "";
    this.pasteMessage = null;
  }

  setPasteText(v: string) {
    this.pasteText = v;
    this.pasteMessage = null;
  }

  /**
   * Put the clipboard's text in the paste box. A browser may refuse to let a
   * page read the clipboard (permissions, plain http); then the box says to
   * paste with Ctrl+V instead.
   */
  async readClipboard(): Promise<void> {
    const clipboard = (globalThis.navigator as Navigator | undefined)?.clipboard;
    // A sign-out while the browser is asked closes the paste with the form.
    const ended = sessionMark(this.rootStore);
    try {
      if (!clipboard?.readText) throw new Error("no clipboard");
      const text = await clipboard.readText();
      // No Feathers call, so the session guard never ends it: the text must not
      // land in the paste box of a form the purge has closed.
      if (ended()) return;
      runInAction(() => {
        this.pasteText = text;
        this.pasteMessage = text.trim() ? null : "The clipboard is empty.";
      });
    } catch {
      if (ended()) return;
      runInAction(() => {
        this.pasteMessage = "This browser did not let the page read the clipboard. Click in the box and paste with Ctrl+V (⌘V).";
      });
    }
  }

  /**
   * Add the pasted rows: `append` after the existing rows (an empty grid's
   * blank starter row is dropped), `replace` instead of them.
   */
  applyPaste(mode: "append" | "replace") {
    if (this.frozen) return;
    const { rows, skipped } = parsePastedEntries(this.pasteText);
    if (rows.length === 0) {
      this.pasteMessage = skipped
        ? `None of the ${skipped} line${skipped === 1 ? "" : "s"} had two columns (source value, then target value).`
        : "There is nothing to add: paste two columns, source value then target value.";
      return;
    }
    const kept = mode === "replace" ? [] : this.rows.filter((r) => r.source.trim() || r.target.trim());
    const total = kept.length + rows.length;
    if (total > LOOKUP_ENTRIES_MAX) {
      this.pasteMessage = `That would make ${total.toLocaleString()} entries; a lookup table holds at most ${LOOKUP_ENTRIES_MAX.toLocaleString()}. Nothing was added.`;
      return;
    }
    this.rows = [...kept, ...rows.map((r) => ({ key: newRowKey(), ...r }))];
    this.page = mode === "replace" ? 0 : Math.floor(kept.length / LOOKUP_ROWS_PER_PAGE);
    this.pasteOpen = false;
    this.pasteText = "";
    this.pasteMessage = null;
    this.notice = null;
    if (skipped > 0) {
      this.formError = `${rows.length} row${rows.length === 1 ? "" : "s"} added; ${skipped} line${skipped === 1 ? "" : "s"} had only one column and ${skipped === 1 ? "was" : "were"} skipped.`;
    } else {
      this.formError = null;
    }
  }

  clearNotice() {
    this.notice = null;
  }

  clearActionError() {
    this.actionError = null;
  }

  // ---- Save / delete -------------------------------------------------------

  /**
   * Create, or edit. An edit names the selection and sends the `updatedAt` it
   * was loaded from; a concurrent save is a 409 `record-changed`, which closes
   * the form, reloads the list with the other person's version and says so.
   */
  async save(): Promise<LookupSaveOutcome> {
    const problem = this.entriesProblem;
    if (!this.formName.trim() || problem) {
      this.formError = problem ?? "A lookup table needs a name.";
      return "failed";
    }
    if (this.entriesLoading || !this.entriesRead) return "failed";
    const service = this.service;
    const selection = this.selection;
    if (!service || !selection?.hasProfile) {
      this.formError = "Open a profile first.";
      return "failed";
    }
    const query = { ...selection.selectionQuery };
    const name = this.formName.trim();
    const description = this.formDescription.trim() || null;
    const entries = toEntriesPayload(this.rows);
    const editingId = this.editingTableId;
    const epoch = this.formEpoch;
    const stale = () => epoch !== this.formEpoch || !sameId(this.profileId, query.profileId);
    this.saving = true;
    this.formError = null;
    try {
      if (editingId) {
        const patch: Record<string, unknown> = { name, description, entries };
        if (this.editingUpdatedAt) patch.expectUpdatedAt = this.editingUpdatedAt;
        await service.patch(editingId, patch as never, { query });
      } else {
        await service.create({ name, description, entries, scope: this.formScope, ...query } as never);
      }
      if (stale()) return "saved";
      runInAction(() => {
        this.saving = false;
        this.closeForm();
        this.notice = null;
      });
      await this.load();
      return "saved";
    } catch (err) {
      if (stale() || isSessionEnded(err)) return "failed";
      if (isRecordChanged(err)) {
        runInAction(() => {
          this.saving = false;
          this.closeForm();
          this.notice = lookupRecordChangedNotice(name);
        });
        await this.load();
        return "conflict";
      }
      runInAction(() => {
        this.formError = editorFailure(this.rootStore, err, query.profileId ?? null, "The lookup table could not be saved.");
      });
      return "failed";
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /** Delete a table with its entries (the caller confirms first). */
  async remove(table: DataMapperLookupTable): Promise<boolean> {
    if (this.saving) return false;
    this.actionError = null;
    this.notice = null;
    const query = { ...(this.selection?.selectionQuery ?? {}) };
    try {
      if (!this.service) throw new Error("The Data Mapper service is not available in this build.");
      await this.service.remove(table.id, { query });
      if (sameId(this.editingTableId, table.id)) this.closeForm();
      await this.load();
      return true;
    } catch (err) {
      // Ended by a sign-out: no error, and no re-read in the next session.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError = editorFailure(this.rootStore, err, query.profileId ?? null, `"${table.name}" could not be deleted.`);
      });
      await this.load();
      return false;
    }
  }

  /** Route exit: drop the form and messages (the selection stays). */
  reset() {
    this.loadSeq++;
    this.loading = false;
    this.error = null;
    this.actionError = null;
    this.notice = null;
    this.saving = false;
    this.closeForm();
  }

  /**
   * Everything reset() keeps across this wizard's route changes goes too: on
   * sign-out it was the previous person's. Called by RootStore.purgeUserData,
   * which prefers this to reset().
   */
  resetForSignOut() {
    this.reset();
    this.loaded = false;
    this.formName = "";
    this.formDescription = "";
    this.formScope = "profile";
    this.entriesRead = false;
  }
}

export default DataMapperLookupTablesStore;
